import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox } from "@mui/material";

import { doc, getDoc } from "firebase/firestore";
import { format } from "date-fns";
import { db } from "../../firebase";
import TopTitleFormComponent from "./TopTitleFormComponent";
import { StandardDrawerFooter } from "./StandardDrawer";
import ReactToPrint from "react-to-print";
import "./AdmissionDocumentRedesign.css";
import FormContentSkeleton from "./FormContentSkeleton";

function ViewClientOrientationCheckList(props) {
  const { clientId, closemodal, firstName, lastName } = props;
  const [data, setData] = useState(null);
  const printRef = useRef();
  const [signaturePic, setSignaturePic] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const docRef = doc(db, "ClientOrientationChecklist", clientId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const fetchedData = docSnap.data();
        setData(fetchedData);
        if (fetchedData.signatureURL) {
          setSignaturePic(fetchedData.signatureURL);
        }
      } else {
        console.log("No checklist found for this client.");
      }
    } catch (error) {
      console.error("Error fetching checklist:", error);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData().finally(() => setIsDataLoading(false));
  }, [clientId, fetchData]);

  function formatDate(timestamp) {
    if (!timestamp || !timestamp.toDate) return "";
    return format(timestamp.toDate(), "dd MMM yyyy, HH:mm");
  }

  // Même structure que dans AddClientOrientationChecklist
  const checklistSections = [
    {
      title: "Initial Introduction",
      items: [
        "Welcome the client and introduce them to the staff.",
        "Medical Office and Staff/ Support Office and Staff/ Clinical Operation Office and Staff/ F&B Staff/ Housekeeping",
      ],
    },
    {
      title: "Provide a tour of the facility",
      items: [
        "Common areas (lounges, dining hall, etc.)",
        "Facilitation rooms (First month & Second)",
        "Gym (On site & Outings)",
        "Casada areas",
      ],
    },
    {
      title: "Documentation and Paperwork in Bodhi",
      items: [
        "Complete intake forms (and Photo)",
        "Review and sign consent forms (and ROI)",
        "Provide a copy of the client handbook (in Room)",
        "Emergency Contact Form",
        "Review the code of conduct",
        "Explain the consequences of rule violations",
        "Emergency procedures and safety protocols",
        "Confidentiality policies",
      ],
    },
    {
      title: "Schedule Overview",
      items: [
        "Daily routine (e.g., wake-up time, Participation)",
        "Program sessions schedule",
        "Sunday outings details and timings",
        "Therapy sessions timing",
        "Introduction to the wellness program (e.g., yoga, meditation)",
      ],
    },
    {
      title: "Meal Times",
      items: [
        "Breakfast: 8:00 AM - 8:30 AM",
        "Lunch: 12:00 PM - 1:00 PM",
        "Dinner: 6:00 PM - 7:00 PM",
      ],
    },
    {
      title: "Program and Therapy Sessions",
      items: [
        "Individual therapy sessions",
        "Group therapy sessions",
        "One-on-one support sessions",
      ],
    },
    {
      title: "Workbook Material",
      items: [
        "Provide and explain the workbook materials",
        "Guide on how to use and complete the workbooks",
        "Schedule for workbook review sessions (1 on 1 Support session)",
      ],
    },
    {
      title: "Support Systems",
      items: [
        "Introduce the assigned therapist",
        "Meet the support staff",
        "Explanation of peer support groups",
      ],
    },
    {
      title: "General Information",
      items: [
        "Laundry schedule and facilities",
        "Visitor policies and on site rules",
        "Mail and communication guidelines",
      ],
    },
    {
      title: "Final Steps",
      items: [
        "Answer any questions the client may have",
        "Provide a contact list for staff members",
        "Schedule the first therapy session",
        "Ensure the client feels settled and supported",
      ],
    },
  ];

  return (
    <>
      <style>
        {`
          @media print {
            #printableContent {
              height: auto !important;
              overflow-y: visible !important;
            }
          }
        `}
      </style>
      <div className="admission-document-shell orientation-form-shell">
        <TopTitleFormComponent
            title="View Client Orientation Checklist"
            eyebrow="ADMISSION FORM"
            subtitle={`${firstName || "Client"} ${lastName || ""}`.trim()}
            closemodal={closemodal}
        />
        <div id="printableContent" className={`admission-document-body ${isDataLoading ? "form-content-loading" : ""}`}
          ref={printRef}
        >
          <FormContentSkeleton rows={5} />
          <h3 className="admission-document-client-name">
            Client: {firstName} {lastName}
          </h3>
          {data && data.signedOn && (
            <p>Signed on: {formatDate(data.signedOn)}</p>
          )}

          {data && data.responses ? (
            checklistSections.map((section, sIndex) => (
              <Box key={sIndex} className="admission-document-section" sx={{ marginBottom: "15px" }}>
                <h4>{section.title}</h4>
                {section.items.map((item, iIndex) => {
                  const key = `${sIndex}_${iIndex}`;
                  return (
                    <Box
                      key={key}
                      className="admission-document-check-row"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "5px",
                      }}
                    >
                      <Checkbox
                        checked={data.responses[key] || false}
                        disabled
                      />
                      <span>{item}</span>
                    </Box>
                  );
                })}
              </Box>
            ))
          ) : (
            <p>No checklist data found.</p>
          )}
          {signaturePic && (
            <div
              className="signature-container admission-document-signature admission-document-signature-view"
              style={{
                marginTop: "20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                Client Signature:
              </div>
              <img
                alt="Client Signature"
                src={signaturePic}
                style={{
                  border: "1px solid #ccc",
                  width: "200px",
                }}
              />
            </div>
          )}
        </div>
        <StandardDrawerFooter className="admission-document-footer">
          <>
            <Button
              variant="contained"
              style={{ backgroundColor: "white", color: "black" }}
              onClick={props.closemodal}
            >
              Cancel
            </Button>

            <ReactToPrint
              trigger={() => (
                <Button variant="contained" style={{ marginLeft: "20px" }}>
                  Print
                </Button>
              )}
              content={() => printRef.current}
              onAfterPrint={props.closemodal}
            />

            {/* <Button
            variant="contained"
            onClick={handlePrint}
            style={{ marginLeft: "20px" }}
          >
            Print
          </Button> */}
          </>
        </StandardDrawerFooter>
      </div>
    </>
  );
}

export default ViewClientOrientationCheckList;
